// The app's routes. Each page's module loads lazily, so it stays a chunk of its own. The server knows these paths too:
// the build writes them into wwwroot/dist/spa-manifest.json (app/prerender.tsx), and the shell is served only for a
// path listed there. Any other path is a 404 (see NotFound).
// Paths ignore case, as the server's do.

import type { ComponentType } from "react";
import { matchRoutes, type RouteObject } from "react-router";
import { AppLayout } from "./AppLayout";
import { AppRoot } from "./AppRoot";
import { AuthLayout } from "./AuthLayout";
import { AUTH_BODY_CLASS, MAIN_BODY_CLASS, type RouteHandle } from "./handles";
import { NOT_FOUND_ROUTE_ID, NotFound } from "./NotFound";
import { RequireAdmin, RequireAuth } from "./RequireAuth";
// In the main bundle, not a chunk of their own: NotFound draws the 404 page
import { ErrorPage, NotFoundPage } from "../routes/errors";
import { SPADES_LESSONS } from "../lessons/catalog";
import { useHydrated } from "./session";

export type { RouteHandle } from "./handles";

type PageHandle = Omit<RouteHandle, "path" | "layout"> & { title: string };

/**
 * A page that isn't prerendered is drawn in the browser only. The build renders its layout around nothing (so the
 * shell serves the navbar and frame at once, as the Razor pages did), and the browser hydrates that before drawing
 * the page.
 */
export function clientOnly(Page: ComponentType): ComponentType {
    return function ClientOnlyPage() {
        return useHydrated() ? <Page /> : null;
    };
}

/** A page for signed-in visitors only (RequireAuth). */
export function signedInOnly(Page: ComponentType): ComponentType {
    return function SignedInOnlyPage() {
        return <RequireAuth><Page /></RequireAuth>;
    };
}

/** A page for admins only (RequireAdmin). */
export function adminOnly(Page: ComponentType): ComponentType {
    return function AdminOnlyPage() {
        return <RequireAdmin><Page /></RequireAdmin>;
    };
}

function guarded(Page: ComponentType, handle: PageHandle): ComponentType {
    if (handle.requiresAdmin)
        return adminOnly(Page);
    if (handle.requiresSignIn)
        return signedInOnly(Page);

    return handle.prerender ? Page : clientOnly(Page);
}

/** A page: its path, its lazily loaded component, and its title and the rest of its handle. */
export function page(path: string, load: () => Promise<ComponentType>, handle: PageHandle): RouteObject {
    if ((handle.requiresSignIn || handle.requiresAdmin) && handle.prerender)
        throw new Error(`"${path}" is for signed-in visitors, so it can't be prerendered.`);

    return {
        path,
        lazy: async () => ({ Component: guarded(await load(), handle) }),
        handle: { path, ...handle } satisfies RouteHandle,
    };
}

/** The game menus: Study, Train, Tools and Play for each game. */
const menuRoutes: RouteObject[] = [
    page("/Study/Spades", async () => (await import("../routes/menus/study")).StudySpadesPage, {
        title: "Study Spades Strategy",
        description: "Spades lessons: the finesse (tenaces, the assisted finesse, finesse refusal) and trump promotion, each with a review quiz, with bidding, leading and counting lessons to come.",
        prerender: true,
    }),
    page("/Study/Hearts", async () => (await import("../routes/menus/study")).StudyHeartsPage, {
        title: "Study Hearts Strategy",
        description: "Hearts lessons coming to Card Game Academy: the rules, basic strategy, counting cards, self defense, playing from behind, passing, the moon and winning more tricks.",
        prerender: true,
    }),
    page("/Study/Euchre", async () => (await import("../routes/menus/study")).StudyEuchrePage, {
        title: "Study Euchre Strategy",
        description: "Euchre lessons coming to Card Game Academy: the rules, ordering up, playing a hand as declarer, and counting cards.",
        prerender: true,
    }),
    page("/Train/Spades", async () => (await import("../routes/menus/train")).TrainSpadesPage, {
        title: "Train Spades Skills",
        description: "Train your Spades skills: rated puzzles from real games, and nil exercises for covering your partner's nil, attacking the opponents' nil and playing as the nil bidder.",
        prerender: true,
    }),
    page("/Train/Hearts", async () => (await import("../routes/menus/train")).TrainHeartsPage, {
        title: "Train Hearts Skills",
        description: "Hearts training coming to Card Game Academy: rated counting challenges and puzzles, card counting exercises, and stopping or shooting the moon.",
        prerender: true,
    }),
    page("/Train/Euchre", async () => (await import("../routes/menus/train")).TrainEuchrePage, {
        title: "Train Euchre Skills",
        description: "Euchre training coming to Card Game Academy: rated counting challenges and puzzles, card counting exercises, and stopping a loner or going alone.",
        prerender: true,
    }),
    page("/Tools/Spades", async () => (await import("../routes/menus/tools")).ToolsSpadesPage, {
        title: "Spades Tools Menu",
        description: "Free Spades tools: Max Tricks, Min Tricks and Nil calculators for bidding, plus AI replay analysis, a hand solver and a replay maker for the hands you play.",
        prerender: true,
    }),
    page("/Tools/Hearts", async () => (await import("../routes/menus/tools")).ToolsHeartsPage, {
        title: "Hearts Tools Menu",
        description: "Hearts tools coming to Card Game Academy, starting with a replay maker for sharing a hand from start to finish.",
        prerender: true,
    }),
    page("/Tools/Euchre", async () => (await import("../routes/menus/tools")).ToolsEuchrePage, {
        title: "Euchre Tools Menu",
        description: "Euchre tools coming to Card Game Academy, starting with a replay maker for sharing a hand from start to finish.",
        prerender: true,
    }),
    page("/Play/Spades", async () => (await import("../routes/menus/play")).PlaySpadesPage, {
        title: "Play Spades",
        description: "Play Spades online: practice games against bots, casual games with other players, and daily solo duplicate Spades tournaments with their results.",
        prerender: true,
    }),
    page("/Play/Hearts", async () => (await import("../routes/menus/play")).PlayHeartsPage, {
        title: "Play Hearts",
        description: "Play Hearts on Card Game Academy: games against bots, and casual and rated games online, are coming soon.",
        prerender: true,
    }),
    page("/Play/Euchre", async () => (await import("../routes/menus/play")).PlayEuchrePage, {
        title: "Play Euchre",
        description: "Play Euchre on Card Game Academy: games against bots, and casual and rated games online, are coming soon.",
        prerender: true,
    }),
];

/**
 * The Spades lessons (Phase 7b), one route each so that each is prerendered with its text. The page's module is shared;
 * each lesson's data is its own chunk, loaded with the route.
 */
const lessonRoutes: RouteObject[] = SPADES_LESSONS.map(lesson =>
    page(lesson.path, async () => (await import("../routes/spades/lesson")).loadLessonPage(lesson), {
        title: lesson.title,
        description: lesson.description,
        prerender: true,
    }));

/** The sign-in pages, in _AuthLayout. A signed-in visitor is sent from Login and Register to the error page, as before. */
const accountRoutes: RouteObject[] = [
    page("/Account/Login", async () => (await import("../routes/account/login")).LoginPage, {
        title: "Log in",
        description: "Log in to your free Card Game Academy account to play Spades online and keep your replays, ratings and settings.",
        prerender: true,
        signedInRedirect: "/Error",
        signedInMessage: { title: "Already Logged in", message: "You are already logged in." },
    }),
    page("/Account/Register", async () => (await import("../routes/account/register")).RegisterPage, {
        title: "Register",
        description: "Create a free Card Game Academy account to play Spades online, keep the replays of your games and save your settings.",
        prerender: true,
        signedInRedirect: "/Error",
        signedInMessage: { title: "Already Logged in", message: "If you would like to register a new account, please log out first." },
    }),
    page("/Account/ForgotPassword", async () => (await import("../routes/account/password")).ForgotPasswordPage, {
        title: "Forgot Password",
        description: "Forgot your Card Game Academy password? Enter your email address and we'll send you a link to reset it.",
        prerender: true,
    }),
    // The password reset email's link. The Razor page had no title (" - Card Game Academy").
    page("/Account/ResetPassword", async () => (await import("../routes/account/password")).ResetPasswordPage, {
        title: "Reset Password",
    }),
];

export const routes: RouteObject[] = [
    {
        Component: AppRoot,
        children: [
            {
                // _Layout: the navbar, the error box, the page
                Component: AppLayout,
                handle: { layout: "main", bodyClass: MAIN_BODY_CLASS } satisfies RouteHandle,
                children: [
                    page("/Home", async () => (await import("../routes/home/main-menu")).MainMenuPage, {
                        title: "Main Menu",
                        description: "Choose a game to study, train, use the tools for or play (Hearts, Spades or Euchre), and read the latest Card Game Academy news.",
                        prerender: true,
                    }),
                    page("/Home/Help", async () => (await import("../routes/home/help")).HelpPage, {
                        title: "Help",
                        description: "Answers to common questions about Card Game Academy: how the Spades Max Tricks, Min Tricks and Nil calculators work, and how to contact the site owner.",
                        prerender: true,
                    }),
                    page("/Home/DuplicateSpadesRules", async () => (await import("../routes/home/duplicate-spades-rules")).DuplicateSpadesRulesPage, {
                        title: "Duplicate Spades Rules",
                        description: "How duplicate Spades tournaments work on Card Game Academy: scoring each board, match points, a worked example, and the solo and team entry formats.",
                        prerender: true,
                    }),
                    page("/Home/Contact", async () => (await import("../routes/home/contact")).ContactPage, {
                        title: "Contact Us",
                        description: "Send a message to Card Game Academy: a question, feedback, a bug report or a replay you'd like us to look at. We try to reply within 72 hours.",
                        prerender: true,
                    }),
                    ...menuRoutes,
                    // Signed-in visitors only: the server sends anyone else to the login page on a page load, the app
                    // after an in-app navigation
                    page("/Account/Profile", async () => (await import("../routes/account/profile")).ProfilePage, {
                        title: "Your Profile",
                        requiresSignIn: true,
                        prefetch: async context => (await import("../routes/account/profile")).prefetchProfile(context),
                    }),
                    // Admins only: anyone else gets the 404 page (the server's on a page load; routesFor leaves the page
                    // out of their app)
                    page("/Admin", async () => (await import("../routes/admin/admin")).AdminPage, {
                        title: "Admin Management",
                        requiresAdmin: true,
                    }),
                    // The Admin Lesson Maker (docs/admin-lesson-maker-plan.md, A1): opened from the Admin page's Spades tab,
                    // the navbar labelled Admin as that page's is (not the {game}'s "Spades")
                    page("/Admin/Spades/LessonMaker", async () => (await import("../routes/admin/lesson-maker")).LessonMakerPage, {
                        title: "Lesson Maker",
                        navbarText: "Admin",
                        requiresAdmin: true,
                    }),
                    // The board pages (Phase 7). The lessons are prerendered; the others are drawn in the browser, and
                    // the replay viewer's navbar is labelled by the Razor controller, as _Navbar did. Puzzles is for
                    // signed-in visitors only, as [Authorize] made it; the Hand Solver and Replay Analysis are open to anyone, as before.
                    // The Replay Maker saves the replay it makes as the visitor's, so it's for signed-in visitors only too.
                    ...lessonRoutes,
                    page("/Replays/Spades/:id", async () => (await import("../routes/spades/replay-viewer")).ReplayViewerPage, {
                        title: "Spades Replay",
                        navbarText: "Replays",
                        prefetch: async context => (await import("../routes/spades/replay-viewer")).prefetchReplay(context),
                    }),
                    // Play Again (docs/play-again-plan.md): a private one-hand game of a replay's hand against the bots.
                    // Reached from a replay (no menu lists it); signed-in visitors only, as every game hub is.
                    page("/Replays/Spades/PlayAgain/:id", async () => (await import("../routes/spades/play-again")).PlayAgainPage, {
                        title: "Play Again",
                        requiresSignIn: true,
                    }),
                    page("/Train/Spades/Puzzles", async () => (await import("../routes/spades/puzzles")).PuzzlesPage, {
                        title: "Spades Puzzles",
                        requiresSignIn: true,
                    }),
                    // Counting exercises (docs/counting-exercises-plan.md, phase 4): the rated one, and a practice page
                    // per category. Signed-in visitors only, as the API is [Authorize].
                    page("/Train/Spades/Counting", async () => (await import("../routes/spades/counting")).RatedCountingPage, {
                        title: "Spades Counting",
                        requiresSignIn: true,
                    }),
                    page("/Train/Spades/Counting/Suits", async () => (await import("../routes/spades/counting")).SuitsCountingPage, {
                        title: "Spades Counting Practice: Suits",
                        requiresSignIn: true,
                    }),
                    page("/Train/Spades/Counting/HighCards", async () => (await import("../routes/spades/counting")).HighCardsCountingPage, {
                        title: "Spades Counting Practice: High Cards",
                        requiresSignIn: true,
                    }),
                    page("/Train/Spades/Counting/LowCards", async () => (await import("../routes/spades/counting")).LowCardsCountingPage, {
                        title: "Spades Counting Practice: Low Cards",
                        requiresSignIn: true,
                    }),
                    page("/Train/Spades/Counting/OthersHands", async () => (await import("../routes/spades/counting")).OthersHandsCountingPage, {
                        title: "Spades Counting Practice: Others' Hands",
                        requiresSignIn: true,
                    }),
                    page("/Tools/Spades/HandSolver/:id", async () => (await import("../routes/spades/hand-solver")).HandSolverPage, {
                        title: "Spades Hand Solver",
                    }),
                    page("/Tools/Spades/ReplayMaker", async () => (await import("../routes/spades/replay-maker")).ReplayMakerPage, {
                        title: "Spades Replay Maker",
                        requiresSignIn: true,
                    }),
                    page("/Tools/Spades/ReplayAnalysis/:id", async () => (await import("../routes/spades/replay-analysis")).ReplayAnalysisPage, {
                        title: "Spades Replay Analysis",
                    }),
                    // The calculators are public and have no data to wait for, so they're prerendered (7f)
                    page("/Tools/Spades/MaxTricksCalculator", async () => (await import("../routes/spades/calculators")).MaxTricksCalculatorPage, {
                        title: "Spades Maximum Tricks Calculator",
                        description: "Free Spades bidding calculator: pick a 13-card hand and see how often it takes each number of tricks over 10,000 playouts, and which bid has the best EV.",
                        prerender: true,
                    }),
                    page("/Tools/Spades/MinTricksCalculator", async () => (await import("../routes/spades/calculators")).MinTricksCalculatorPage, {
                        title: "Spades Minimum Tricks Calculator",
                        description: "Free Spades calculator for hands played to take as few tricks as possible: pick 13 cards and see the odds of each trick count and the best EV bid.",
                        prerender: true,
                    }),
                    page("/Tools/Spades/NilCalculator", async () => (await import("../routes/spades/calculators")).NilCalculatorPage, {
                        title: "Spades Nil Calculator",
                        description: "Free Spades nil calculator: pick your 13 cards and the bids so far to see how often a nil makes, its expected value, and the suit most likely to set it.",
                        prerender: true,
                    }),
                    // The live game pages (Phase 8): signed-in visitors only, as [Authorize] made them
                    page("/Play/Spades/Practice", async () => (await import("../routes/spades/practice")).PracticePage, {
                        title: "Spades: Practice against the computer",
                        requiresSignIn: true,
                    }),
                    // The casual lobby and its tables (8c), with the Razor views' title; neither shows the seated
                    // banner (10d)
                    page("/Play/Spades/Casual", async () => (await import("../routes/spades/lobby")).LobbyPage, {
                        title: "Play Spades Online",
                        requiresSignIn: true,
                        hidesSeatedBanner: true,
                    }),
                    page("/Play/Spades/Table/:tableId", async () => (await import("../routes/spades/table")).TablePage, {
                        title: "Play Spades Online",
                        requiresSignIn: true,
                        hidesSeatedBanner: true,
                    }),
                    // Solo duplicate (8d): the table for signed-in visitors only, the results for anyone (as the Razor
                    // pages), drawn in the browser (they change daily). _Navbar showed "Play" on the table, whose route
                    // had no {game}. The team entry's table (docs/team-duplicate-plan.md) is the same page's chunk.
                    page("/Play/Spades/Duplicate/Solo", async () => (await import("../routes/spades/duplicate")).SoloDuplicatePage, {
                        title: "Play Duplicate Spades Online",
                        navbarText: "Play",
                        requiresSignIn: true,
                    }),
                    page("/Play/Spades/Duplicate/Team", async () => (await import("../routes/spades/duplicate")).TeamDuplicatePage, {
                        title: "Play Team Duplicate Spades Online",
                        navbarText: "Play",
                        requiresSignIn: true,
                    }),
                    page("/Play/Spades/Duplicate/Solo/Results/:date?", async () => (await import("../routes/spades/duplicate-results")).SoloResultsPage, {
                        title: "View Duplicate Results",
                        prefetch: async context => (await import("../routes/spades/duplicate-results")).prefetchResults(context, "solo"),
                    }),
                    // The team tournament's results: the same page's chunk, for anyone too
                    page("/Play/Spades/Duplicate/Team/Results/:date?", async () => (await import("../routes/spades/duplicate-results")).TeamResultsPage, {
                        title: "View Team Duplicate Results",
                        prefetch: async context => (await import("../routes/spades/duplicate-results")).prefetchResults(context, "team"),
                    }),
                    // The nil exercises (8b): a page per category, with the Razor views' titles
                    page("/Train/Spades/AsNil", async () => (await import("../routes/spades/nil-exercises")).AsNilPage, {
                        title: "Spades Practice As Nil",
                        requiresSignIn: true,
                    }),
                    page("/Train/Spades/NilCovering", async () => (await import("../routes/spades/nil-exercises")).NilCoveringPage, {
                        title: "Spades Nil Cover Practice",
                        requiresSignIn: true,
                    }),
                    page("/Train/Spades/AttackingNils", async () => (await import("../routes/spades/nil-exercises")).AttackingNilsPage, {
                        title: "Spades Practice Attacking Nils",
                        requiresSignIn: true,
                    }),
                    // The registration email's link: in _Layout's frame, as the Razor view was
                    page("/Account/ConfirmEmail", async () => (await import("../routes/account/confirm-email")).ConfirmEmailPage, {
                        title: "Email Registration Complete",
                    }),
                    // The error pages aren't for search engines. /Error/404 comes before /Error/:code, the order in which
                    // the server tries them.
                    page("/Error", async () => ErrorPage, { title: "Error" }),
                    page("/Error/404", async () => NotFoundPage, { title: "404 - Page Not Found" }),
                    page("/Error/:code", async () => ErrorPage, { title: "Error" }),
                    // A path with no page: the 404 page (the server's /Error/404, whose title and navbar label these are)
                    {
                        id: NOT_FOUND_ROUTE_ID,
                        path: "*",
                        Component: NotFound,
                        handle: { title: "404 - Page Not Found", navbarText: "Error" } satisfies RouteHandle,
                    },
                ],
            },
            {
                // _AuthLayout: the landing pages, and the sign-in pages (Phase 5)
                Component: AuthLayout,
                handle: { layout: "auth", bodyClass: AUTH_BODY_CLASS } satisfies RouteHandle,
                children: [
                    page("/", async () => (await import("../routes/home/landing")).LandingPage, {
                        title: "Home",
                        description: "Card Game Academy, the ultimate resource for card games: learn the rules of Hearts, Spades and Euchre, study strategy, solve puzzles and play.",
                        prerender: true,
                        bodyClass: "",
                        hideNavbar: true,
                        signedInRedirect: "/Home",
                    }),
                    page("/Home/ComingSoon", async () => (await import("../routes/home/landing")).ComingSoonPage, {
                        title: "Coming Soon",
                        description: "Rated play on Card Game Academy is coming soon. In the meantime, register, view a demo of the replay analysis, contact us or explore the site.",
                        prerender: true,
                        bodyClass: "",
                        hideNavbar: true,
                    }),
                    ...accountRoutes,
                ],
            },
        ],
    },
];

/**
 * The routes the app is started with for this visitor: an admin's are all of them; anyone else's leave out the admin
 * pages, so for them the admin page's address is the catch-all's (NotFound). On a page load the server has answered
 * it with its 404 page, which the catch-all draws (navbar label included, so hydration matches); a link to it loads in
 * full, and the server answers the same. Someone who becomes an admin by signing in loads the page in full once.
 */
export function routesFor(session: { isAdmin: boolean }, routeList: RouteObject[] = routes): RouteObject[] {
    if (session.isAdmin)
        return routeList;

    return routeList
        .filter(route => !(route.handle as RouteHandle | undefined)?.requiresAdmin)
        .map(route => route.children ? { ...route, children: routesFor(session, route.children) } : route) as RouteObject[];
}

/** True when the app has a page for this path (not the catch-all's 404). */
export function isAppPath(pathname: string, routeList: RouteObject[] = routes): boolean {
    const matches = matchRoutes(routeList, pathname);
    return matches != null && matches[matches.length - 1].route.id !== NOT_FOUND_ROUTE_ID;
}

/**
 * Loads the lazy modules of the routes matching this path. The client does this before hydrating, so its first
 * render draws the same page the prerendered HTML holds (rather than nothing while the module loads).
 */
export async function loadLazyRoutes(pathname: string, routeList: RouteObject[] = routes): Promise<void> {
    const matches = matchRoutes(routeList, pathname) ?? [];

    await Promise.all(matches.map(async ({ route }) => {
        if (typeof route.lazy === "function") {
            const loaded = await route.lazy();
            Object.assign(route, loaded);
            route.lazy = undefined;
        }
    }));
}
